import { es, enUS } from 'date-fns/locale';
import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DateRangePickerProps, DateRange, DateRangePicker as DateRangePickerAdvanced } from 'react-date-range';
import { createPortal } from 'react-dom';
// TODO: this can be  usefull in Icon component
import Media from 'react-media';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { twMerge } from 'tailwind-merge';
import Button from '../button/button';
import Icon from '../icon/icon';
import { inputSize, inputStyle } from '../input/input';
import TextField, { TextFieldProps } from '../text-field/textField';
type CalendarPosition = 'top' | 'button' | 'left' | 'right' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
export interface IProps {
    className?: string;
    calendarProps: DateRangePickerProps;
    inputProps: TextFieldProps;
    isOpen: boolean;
    onCancel?: () => void;
    onAccept?: () => void;
    onClose?: () => void;
    onToggle?: (nextOpen: boolean) => void;
    testId?: string;
    calendarPosition?: CalendarPosition;
    withCalendarOptions?: boolean;
    locale?: 'es' | 'en';
    error?: string;
    disabled?: boolean;
}

export { getStaticRanges } from './static-ranges';

export const DateRangePicker: FC<IProps & DateRangePickerProps> = ({
    calendarProps,
    inputProps,
    isOpen = false,
    onCancel,
    onAccept,
    testId,
    onClose,
    onToggle,
    calendarPosition,
    withCalendarOptions = false,
    className,
    locale = 'es',
    error,
    disabled,
}) => {
    const inputRef = useRef<HTMLDivElement>(null);
    const portalRef = useRef<HTMLDivElement>(null);
    const [calendarPosition2, setCalendarPosition2] = useState<{ top: number; left: number } | null>(null);
    const [computedPosition, setComputedPosition] = useState<{ top: number; left: number } | null>(null);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();

            if (rect.width === 0 && rect.height === 0) {
                setCalendarPosition2(null);
                setComputedPosition(null);
                return;
            }

            setCalendarPosition2({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
            });
            setComputedPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
            });
        } else if (!isOpen) {
            setCalendarPosition2(null);
            setComputedPosition(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !onClose) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;

            // Check if click is outside both input and portal
            if (
                inputRef.current &&
                !inputRef.current.contains(target) &&
                portalRef.current &&
                !portalRef.current.contains(target)
            ) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    const hexaColorsFromCssVars: Record<inputStyle, string> = {
        primary: getComputedStyle(document.documentElement).getPropertyValue('--primary-500'),
        variant: getComputedStyle(document.documentElement).getPropertyValue('--variant-800'),
    };
    // Normalize input sizes to all UIKit
    const calendarInputSizes: Record<inputSize, number> = {
        sm: 30,
        md: 38,
        lg: 46,
    };

    useLayoutEffect(() => {
        if (!isOpen || !calendarPosition2 || !inputRef.current) {
            return;
        }

        if (!calendarPosition) {
            setComputedPosition(calendarPosition2);
            return;
        }

        const portalElement = portalRef.current;
        if (!portalElement) {
            setComputedPosition(calendarPosition2);
            return;
        }

        const inputRect = inputRef.current.getBoundingClientRect();
        const portalRect = portalElement.getBoundingClientRect();
        const inputHeight = calendarInputSizes[inputProps.size ?? 'md'];

        let top = calendarPosition2.top;
        let left = calendarPosition2.left;

        // Adjust portal coordinates to honour calendarPosition preference.
        switch (calendarPosition) {
            case 'top':
                top = inputRect.top + window.scrollY - portalRect.height - inputHeight;
                break;
            case 'button':
                top = calendarPosition2.top;
                break;
            case 'left':
                left = inputRect.left + window.scrollX + inputRect.width - portalRect.width;
                break;
            case 'right':
                left = inputRect.left + window.scrollX;
                break;
            case 'topLeft':
                top = inputRect.top + window.scrollY - portalRect.height - inputHeight;
                break;
            case 'topRight':
                top = inputRect.top + window.scrollY - portalRect.height - inputHeight;
                left = inputRect.right + window.scrollX - portalRect.width;
                break;
            case 'bottomLeft':
                top = calendarPosition2.top;
                break;
            case 'bottomRight':
                top = calendarPosition2.top;
                left = inputRect.right + window.scrollX - portalRect.width;
                break;
            default:
                break;
        }

        setComputedPosition({ top, left });
    }, [calendarPosition, calendarPosition2, inputProps.size, isOpen]);

    if (disabled) {
        inputProps.onChange = undefined;
        inputProps.onClick = undefined;
        inputProps.onFocus = undefined;
        inputProps.onKeyDown = undefined;
        calendarProps.onChange = undefined;
        calendarProps.onPreviewChange = undefined;
        calendarProps.onRangeFocusChange = undefined;
        calendarProps.onShownDateChange = undefined;
    }

    return (
        <div
            ref={inputRef}
            className={twMerge('relative w-full', className, disabled && 'pointer-events-none opacity-50')}
            data-test-id={testId}
        >
            <TextField
                rounded={'standard'}
                endButton={{
                    iconName: 'ri-calendar-event-line',
                    background: 'transparent',
                    action: () => {
                        if (disabled) {
                            return;
                        }
                        if (onToggle) {
                            onToggle(!isOpen);
                            return;
                        }
                        onClose?.();
                    },
                }}
                {...inputProps}
            />
            {isOpen &&
                calendarPosition2 &&
                createPortal(
                    <div
                        ref={portalRef}
                        className="fixed z-[9999] rounded-lg rounded-tl-none border border-error-300 shadow-[0px_6px_10px_0px_rgba(20,115,247,0.15)]"
                        style={{
                            top: `${(computedPosition ?? calendarPosition2).top}px`,
                            left: `${(computedPosition ?? calendarPosition2).left}px`,
                        }}
                        data-test-id={testId}
                    >
                        <Media queries={{ small: { maxWidth: 834 } }}>
                            {(matches) =>
                                withCalendarOptions ? (
                                    <DateRangePickerAdvanced
                                        {...calendarProps}
                                        months={matches.small ? 1 : 2}
                                        direction="horizontal"
                                        locale={locale === 'es' ? es : enUS}
                                        monthDisplayFormat="LLLL yyyy"
                                        weekStartsOn={1}
                                        dateDisplayFormat={locale === 'es' ? 'dd MMM, yyyy' : 'MMM DD, yyyy'}
                                        rangeColors={[hexaColorsFromCssVars[inputProps.style || 'primary']]}
                                    />
                                ) : (
                                    <DateRange
                                        monthDisplayFormat="LLLL yyyy"
                                        locale={locale === 'es' ? es : enUS}
                                        weekStartsOn={1}
                                        showDateDisplay={false}
                                        showMonthAndYearPickers={false}
                                        dateDisplayFormat={locale === 'es' ? 'dd MMM, yyyy' : 'MMM DD, yyyy'}
                                        months={matches.small ? 1 : 2}
                                        direction="horizontal"
                                        rangeColors={[hexaColorsFromCssVars[inputProps.style || 'primary']]}
                                        {...calendarProps}
                                    />
                                )
                            }
                        </Media>
                        <div className="flex items-center justify-end px-2 py-1 pb-2 bg-white rounded-b-lg w-100 border-t-gray-50 shadow-qm">
                            {error && (
                                <div className="flex space-x-1 text-sm items-center bg-[#FFE7E8] rounded p-2">
                                    <Icon name="ri-alert-line" size={18} className="text-[#FF7079]" />
                                    <div className="text-[#FF333F] me-8">{error}</div>
                                </div>
                            )}
                            {onCancel && (
                                <Button
                                    label="Cancelar"
                                    onClick={onCancel}
                                    dataTestId="cancel-range-selection"
                                    variant="ghost"
                                    color={inputProps.style || 'primary'}
                                    className="ml-3"
                                />
                            )}
                            {onAccept && (
                                <Button
                                    label="Aceptar"
                                    onClick={onAccept}
                                    dataTestId="accept-range-selection"
                                    color={inputProps.style || 'primary'}
                                    className="ml-3"
                                    disabled={!!error}
                                />
                            )}
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    );
};

export default DateRangePicker;
